import React, { useState } from 'react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ProjectColor from '../../js/projectColor';
import { AnimateSpin, Btn, formatCurrency, Text } from '../../components/index';
import { ReactSVG } from 'react-svg';
import textWeight from '../../utils/textWeight';
import { fontSizes } from '../../constants/constants';
import CustomDropdown from '../CustomDropdown';
import { useForm } from 'react-hook-form';
import Icons from '../../assets/icons';
import {
  useMaterialPriceingRange,
  useSaveCostingReportMutation,
} from '../../actions/singleProjectAction';

const TableComponent = ({
  title,
  description,
  columns,
  data,
  buttons,
  isLoading,
  projectID,
  costingStatusReport,
  setCostingReport,
  setCostingStatusReport,
  projectTitle = false,
}) => {
  const { TextWeight } = textWeight();
  const [selectedRates, setSelectedRates] = useState({});
  const [loading, setLoading] = useState(false);
  const allRatesSelected =
    Array.isArray(data) && data.length > 0
      ? data.every((_, idx) => selectedRates[idx] > 0)
      : false;

  const { data: materialPriceingRange } = useMaterialPriceingRange();
  const saveCostingReportMutation = useSaveCostingReportMutation(
    projectID,
    setLoading,
    setCostingReport,
    setCostingStatusReport
  );

  const {
    control,
    formState: { errors },
  } = useForm();

  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(title, 10, 10);

    // ✅ Calculate totals only if those columns exist
    const totalAmount = data?.some((i) => i['Amount in PKR'])
      ? data.reduce((sum, item) => {
          const val = parseFloat(item['Amount in PKR']) || 0;
          return sum + val;
        }, 0)
      : null;

    const totalMohandes = data?.some(
      (i) => i['Total Mohandes'] && i['Total Mohandes'] !== 'N/A'
    )
      ? data.reduce((sum, item) => {
          const val =
            typeof item['Total Mohandes'] === 'number'
              ? item['Total Mohandes']
              : parseFloat(item['Total Mohandes']) || 0;
          return sum + val;
        }, 0)
      : null;

    // ✅ Main Table
    doc.autoTable({
      head: [columns],
      body: data.map((item) => [
        item?.Description,
        item?.Unit,
        item?.Quantity,
        costingStatusReport === 'COMPLETED' ? item['Rate in PKR'] : '',
        costingStatusReport === 'COMPLETED'
          ? formatCurrency(Number(item['Amount in PKR']))
          : '',
        costingStatusReport === 'COMPLETED' ? item['Mohandes Rates'] : '',
        costingStatusReport === 'COMPLETED'
          ? formatCurrency(Number(item['Total Mohandes']))
          : '',
      ]),
      startY: 20,
    });

    // ✅ Add totals if applicable
    const finalY = doc.lastAutoTable.finalY + 5;

    if (totalAmount !== null || totalMohandes !== null) {
      const totalsRow = [
        {
          content: 'Total:', // ✅ no newlines or hidden spacing
          // colSpan: 4,
          styles: {
            halign: 'left',
            fontStyle: 'bold',
            valign: 'middle',
          },
        },
        {
          content: totalAmount !== null ? formatCurrency(totalAmount) : '',
          styles: { halign: 'right', fontStyle: 'bold' },
        },
        { content: '', styles: { halign: 'center' } },
        {
          content: totalMohandes !== null ? formatCurrency(totalMohandes) : '',
          styles: { halign: 'right', fontStyle: 'bold' },
        },
      ];

      doc.autoTable({
        body: [totalsRow],
        startY: finalY,
        theme: 'plain',
        styles: { textColor: [0, 0, 0], fontSize: 11 },
      });
    }

    doc.save(`${title}.pdf`);
  };

  const downloadExcel = () => {
    const workbook = XLSX.utils.book_new();

    // ✅ Calculate totals first
    const totalAmount = data?.some((i) => i['Amount in PKR'])
      ? data.reduce((sum, item) => {
          const val = parseFloat(item['Amount in PKR']) || 0;
          return sum + val;
        }, 0)
      : null;

    const totalMohandes = data?.some(
      (i) => i['Total Mohandes'] && i['Total Mohandes'] !== 'N/A'
    )
      ? data.reduce((sum, item) => {
          const val =
            typeof item['Total Mohandes'] === 'number'
              ? item['Total Mohandes']
              : parseFloat(item['Total Mohandes']) || 0;
          return sum + val;
        }, 0)
      : null;

    // ✅ Main table data
    const worksheetData = [
      columns,
      ...data.map((item) => [
        item?.Description,
        item?.Unit,
        item?.Quantity,
        costingStatusReport === 'COMPLETED' ? item['Rate in PKR'] : '',
        costingStatusReport === 'COMPLETED'
          ? formatCurrency(Number(item['Amount in PKR']))
          : '',
        costingStatusReport === 'COMPLETED' ? item['Mohandes Rates'] : '',
        costingStatusReport === 'COMPLETED'
          ? formatCurrency(Number(item['Total Mohandes']))
          : '',
      ]),
    ];

    // ✅ Add totals row only if needed
    if (totalAmount !== null || totalMohandes !== null) {
      const totalsRow = [
        'Total:',
        '', // Unit
        '', // Quantity
        '', // Rate
        totalAmount !== null ? formatCurrency(totalAmount) : '',
        '', // Mohandes Rate
        totalMohandes !== null ? formatCurrency(totalMohandes) : '',
      ];
      worksheetData.push(totalsRow);
    }

    // ✅ Create sheet
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // ✅ Bold total row
    if (totalAmount !== null || totalMohandes !== null) {
      const totalRowIndex = worksheetData.length; // 1-based index for Excel
      const totalRowRef = `A${totalRowIndex}:G${totalRowIndex}`;
      const range = XLSX.utils.decode_range(totalRowRef);

      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: range.s.r, c: C });
        if (!worksheet[cellAddress]) continue;
        worksheet[cellAddress].s = {
          font: { bold: true },
        };
      }
    }

    // ✅ Append to workbook and download
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Costing Report');
    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `${title}.xlsx`);
  };

  const saveCostingReport = () => {
    if (!allRatesSelected) return;
    setLoading(true);
    const costingReport = data?.map((item, idx) => {
      const matchedMaterial = materialPriceingRange?.data?.data?.find(
        (m) =>
          m.name?.toLowerCase().trim() ===
          item?.Description?.toLowerCase().trim()
      );

      const quantity = item?.Quantity;
      const selectedRate = parseFloat(selectedRates[idx]) || 0;
      const mohandesRate = matchedMaterial?.mohandesPrice
        ? parseFloat(matchedMaterial.mohandesPrice)
        : null;

      const amountInPKR = quantity * selectedRate;
      const totalMohandes = mohandesRate ? quantity * mohandesRate : null;

      return {
        Description: item?.Description ?? '',
        Unit: item?.Unit ?? '',
        Quantity: quantity,
        'Rate in PKR': selectedRate > 0 ? selectedRate : 'N/A',
        'Amount in PKR': selectedRate > 0 ? amountInPKR : 'N/A',
        'Mohandes Rates': mohandesRate ? mohandesRate : 'N/A',
        'Total Mohandes': totalMohandes ? totalMohandes : 'N/A',
      };
    });

    console.log('costingReport', costingReport);

    saveCostingReportMutation.mutate(costingReport);
  };
  return (
    <div>
      {isLoading ? (
        <AnimateSpin />
      ) : (
        <>
          {projectTitle && (
            <div className='mb-2'>
              <Text
                text={`Project: ${projectTitle}`}
                size={fontSizes.h20}
                weight={TextWeight.Raleway_SemiBold}
                color='text-gray-600'
                isTruncate={false}
              />
            </div>
          )}
          <div className='pt-2'>
            <div className='flex items-center gap-3'>
              <span className='inline-block w-1.5 h-6 rounded-full bg-primary'></span>
              <Text
                text={title}
                size={fontSizes.h30}
                weight={TextWeight.Raleway_ExtraBold}
                color='text-black'
                isTruncate={false}
              />
            </div>
            {description && (
              <Text
                text={description}
                size={fontSizes.h16}
                weight={TextWeight.Raleway_Medium}
                color='text-gray-600'
                isTruncate={false}
                className='pl-5 mt-1'
              />
            )}
          </div>
          <div className='overflow-y-scroll hide-scrollbar'>
            <table>
              <thead>
                <tr>
                  {columns.map((col, index) => (
                    <th
                      key={index}
                      className={
                        col === 'Total Mohandes'
                          ? 'pr-3 sm:pr-5 lg:pr-10 xl:pr-14 text-right'
                          : ''
                      }
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.map((item, index) => (
                  <tr key={index}>
                    <td className='flex'>
                      <div
                        style={{
                          backgroundColor:
                            ProjectColor[index % ProjectColor.length].color,
                        }}
                        className='rounded-full mr-3 font-ralewayBold flex justify-center items-center text-secondary w-7 h-7'
                      >
                        {item?.Description?.[0]}
                      </div>
                      {item?.Description}
                    </td>
                    <td>{item?.Unit}</td>
                    <td>{item?.Quantity}</td>
                    {item?.Material && <td>{item?.Material}</td>}
                    {item?.TotalPrice && <td>{item?.TotalPrice} </td>}

                    {costingStatusReport === 'COMPLETED' ? (
                      <td>{item['Rate in PKR']}</td>
                    ) : (
                      <>
                        {columns?.map(
                          (col) =>
                            col === 'Rate in PKR' && (
                              <td
                                className='!pl-0 !py-0 w-44'
                                key={`rate-${index}`}
                              >
                                {(() => {
                                  const matchedMaterial =
                                    materialPriceingRange?.data?.data?.find(
                                      (m) =>
                                        m.name.toLowerCase().trim() ===
                                        item?.Description?.toLowerCase().trim()
                                    );

                                  if (matchedMaterial) {
                                    const { minimumPrice, maximumPrice } =
                                      matchedMaterial;
                                    const priceRange = [];
                                    for (
                                      let p = minimumPrice;
                                      p <= maximumPrice;
                                      p++
                                    ) {
                                      priceRange.push({
                                        label: p.toString(),
                                        value: p,
                                      });
                                    }
                                    return (
                                      <CustomDropdown
                                        control={control}
                                        errors={errors}
                                        rightIcon={Icons.arrowIcon}
                                        options={priceRange}
                                        valueName='label'
                                        valueoption='value'
                                        name={`rate_${index}`}
                                        isLoadingpreFiled={''}
                                        isLoading={isLoading}
                                        onRateChange={(selectedRate) => {
                                          const rateNum =
                                            parseFloat(selectedRate);
                                          setSelectedRates((prev) => ({
                                            ...prev,
                                            [index]: rateNum,
                                          }));
                                        }}
                                      />
                                    );
                                  } else {
                                    return (
                                      <input
                                        type='number'
                                        className='w-full border border-gray-300 rounded-md px-2 py-1'
                                        placeholder='Enter rate'
                                        onChange={(e) => {
                                          const rateNum = parseFloat(
                                            e.target.value
                                          );
                                          setSelectedRates((prev) => ({
                                            ...prev,
                                            [index]: rateNum,
                                          }));
                                        }}
                                      />
                                    );
                                  }
                                })()}
                              </td>
                            )
                        )}
                      </>
                    )}

                    {columns?.map(
                      (col) =>
                        col === 'Amount in PKR' && (
                          <td className='text-right'>
                            {costingStatusReport === 'COMPLETED'
                              ? formatCurrency(item['Amount in PKR'])
                              : formatCurrency(
                                  item?.Quantity * selectedRates[index]
                                )}
                          </td>
                        )
                    )}

                    {costingStatusReport === 'COMPLETED' ? (
                      <td className='text-right'>{item['Mohandes Rates']}</td>
                    ) : (
                      <>
                        {columns?.map(
                          (col) =>
                            col === 'Mohandes Rates' && (
                              <td className='text-right' key={`rates-${index}`}>
                                {(() => {
                                  const matchedMaterial =
                                    materialPriceingRange?.data?.data?.find(
                                      (m) =>
                                        m.name?.toLowerCase().trim() ===
                                        item?.Description?.toLowerCase().trim()
                                    );
                                  return matchedMaterial
                                    ? matchedMaterial.mohandesPrice
                                    : 'N/A';
                                })()}
                              </td>
                            )
                        )}
                      </>
                    )}

                    {columns?.map(
                      (col) =>
                        col === 'Total Mohandes' && (
                          <td
                            key={`total-${index}`}
                            className='text-right pr-3 sm:pr-5 lg:pr-10 xl:pr-14'
                          >
                            {(() => {
                              const matchedMaterial =
                                materialPriceingRange?.data?.data?.find(
                                  (m) =>
                                    m.name?.toLowerCase().trim() ===
                                    item?.Description?.toLowerCase().trim()
                                );

                              const mohandesRate =
                                matchedMaterial?.mohandesPrice;
                              const total =
                                costingStatusReport === 'COMPLETED'
                                  ? item['Total Mohandes']
                                  : item?.Quantity * mohandesRate;

                              return formatCurrency(total);
                            })()}
                          </td>
                        )
                    )}
                  </tr>
                ))}
              </tbody>
              {columns?.map(
                (col) =>
                  col === 'Amount in PKR' ||
                  (col === 'Mohandes Rates' && (
                    <tfoot className='font-semibold bg-[#F5F4F4] border-t-[1px] border-[#CCCBCB]'>
                      <tr>
                        {/* Empty cells to align totals */}
                        <td colSpan={4} className='text-left py-3 pr-3'>
                          Total:
                        </td>

                        {/* ✅ Amount in PKR Total */}
                        <td className='text-right'>
                          {formatCurrency(
                            data?.reduce((sum, item, index) => {
                              const rate =
                                costingStatusReport === 'COMPLETED'
                                  ? parseFloat(item['Rate in PKR'] || 0)
                                  : selectedRates[index] || 0;
                              const amount = (item?.Quantity || 0) * rate;
                              return sum + (isNaN(amount) ? 0 : amount);
                            }, 0)
                          )}
                        </td>

                        {/* Empty column for Mohandes Rates */}
                        <td></td>

                        {/* ✅ Total Mohandes Total */}
                        <td className='text-right pr-3 sm:pr-5 lg:pr-10 xl:pr-14'>
                          {formatCurrency(
                            data?.reduce((sum, item) => {
                              const matchedMaterial =
                                materialPriceingRange?.data?.data?.find(
                                  (m) =>
                                    m.name?.toLowerCase().trim() ===
                                    item?.Description?.toLowerCase().trim()
                                );

                              const mohandesRate =
                                costingStatusReport === 'COMPLETED'
                                  ? parseFloat(item['Mohandes Rates']) || 0
                                  : parseFloat(
                                      matchedMaterial?.mohandesPrice
                                    ) || 0;

                              const quantity = parseFloat(item?.Quantity) || 0;
                              const totalMoh = quantity * mohandesRate;

                              return sum + (isNaN(totalMoh) ? 0 : totalMoh);
                            }, 0)
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  ))
              )}
            </table>
          </div>

          <div className='flex items-center flex-wrap sm:flex-nowrap gap-4'>
            {buttons?.map((icon, index) => (
              <button
                key={index}
                className={`flex items-center px-4 py-3 gap-2 sm:gap-3 shadow-custom-gray rounded-xl ${
                  icon?.isDisabled
                    ? 'cursor-pointer'
                    : `opacity-50 cursor-not-allowed`
                }`}
                onClick={
                  icon?.isDisabled
                    ? index === 0
                      ? downloadPDF
                      : downloadExcel
                    : null
                }
                isDisabled={icon?.isDisabled}
              >
                <ReactSVG
                  src={icon.iconSrc}
                  beforeInjection={(svg) => {
                    svg.classList.add('w-6', 'md:w-8', 'h-auto');
                  }}
                />
                <ReactSVG
                  src={icon.downloadIconSrc}
                  beforeInjection={(svg) => {
                    svg.classList.add('w-6', 'md:w-8', 'h-auto');
                  }}
                />
              </button>
            ))}
            {columns?.map(
              (col) =>
                col === 'Amount in PKR' && (
                  <div
                    className='flex justify-end items-center w-full mt-3 sm:mt-0'
                    key='save-btn'
                  >
                    <Btn
                      type={'button'}
                      isDisabled={
                        !allRatesSelected ||
                        loading ||
                        costingStatusReport === 'COMPLETED'
                      }
                      loading={loading}
                      isprimary={false}
                      weight={TextWeight.Raleway_SemiBold}
                      textSize={fontSizes.h18}
                      text={'Save'}
                      color='text-white'
                      onClick={saveCostingReport}
                      hover={
                        'hover:text-primary hover:bg-white hover:border-primary'
                      }
                      bgColor={'bg-primary border-transparent'}
                    />
                  </div>
                )
            )}
          </div>
        </>
      )}
      {/* Section separator for clearer report boundaries */}
      <div className='my-6 border-t border-gray-200' />
    </div>
  );
};

export default TableComponent;
